import { useState, useEffect } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  FileUpload,
  Icon,
} from '@repo/ui';
import { fileUtil } from '@xtnd/web/shared/utils/fileUtil';
import type { IconName } from '@repo/ui';
import React from 'react';
import { AR_ATTACHMENT_ACCEPT, AR_ATTACHMENT_MAX_SIZE } from '../../constants/attachments';

export interface AttachmentItem {
  filename: string;
  fileKey: string;
  fileSize: number;
  file?: File;
  checked?: boolean;
  category?:string
}

interface AttachmentInputProps {
  readonly items: AttachmentItem[];
  readonly onAdd: (files: File[]) => void;
  readonly onView: (item: AttachmentItem) => void;
  readonly onToggleChecked?: (item: AttachmentItem, checked: boolean) => void;
  readonly loadingFileKey?: string | null;
}

/**
 * Gets the file icon based on file extension
 */
const getFileIcon = (filename: string): IconName => {
  const extension = filename.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return 'HiDocumentText';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'HiPhoto';
    default:
      return 'HiPaperClip';
  }
};

export const AttachmentInput = ({
  items,
  onAdd,
  onView,
  onToggleChecked,
  loadingFileKey,
}: AttachmentInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(
    new Set(items.filter(item => item.checked !== false).map(item => item.fileKey))
  );

  // Sync checked items when items prop changes
  useEffect(() => {
    const newCheckedItems = new Set(
      items.filter(item => item.checked !== false).map(item => item.fileKey)
    );
    setCheckedItems(newCheckedItems);
  }, [items]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleToggleChecked = (item: AttachmentItem, checked: boolean, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the item onClick
    const newCheckedItems = new Set(checkedItems);
    if (checked) {
      newCheckedItems.add(item.fileKey);
    } else {
      newCheckedItems.delete(item.fileKey);
    }
    setCheckedItems(newCheckedItems);
    if (onToggleChecked) {
      onToggleChecked(item, checked);
    }
  };

  const handleItemKeyDown = (e: React.KeyboardEvent, item: AttachmentItem) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView(item);
    }
  };

  const handleAdd = () => {
    setIsOpen(true);
  };

  const handleConfirm = (files: File[]) => {
    onAdd(files);
  };

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor="attachment-input"
        className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white"
      >
        Attachments
        {items.length > 0 && (
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
            ({items.length} {items.length === 1 ? 'file' : 'files'})
          </span>
        )}
      </label>
      {items.length === 0 ? (
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 dark:border-xtnd-dark-600 rounded-md bg-gray-50 dark:bg-xtnd-dark-700 hover:border-xtnd-blue dark:hover:border-xtnd-blue-400 hover:bg-xtnd-light dark:hover:bg-xtnd-dark-600 transition-colors focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2"
        >
          <div className="flex flex-col items-center gap-2 text-center">
            <Icon name="HiPaperClip" className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Click to add attachments</p>
          </div>
        </button>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map(item => {
            const isLoading = loadingFileKey === item.fileKey;
            const isChecked = checkedItems.has(item.fileKey);
            return (
              <div
                key={item.fileKey}
                role="button"
                tabIndex={0}
                onClick={() => !isLoading && onView(item)}
                onKeyDown={e => !isLoading && handleItemKeyDown(e, item)}
                className={`group flex items-center gap-2 px-3 py-2 bg-xtnd-light dark:bg-xtnd-dark-700 border border-xtnd-blue-200 dark:border-xtnd-dark-600 rounded-md hover:border-xtnd-blue dark:hover:border-xtnd-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-1 ${
                  isLoading ? 'cursor-wait opacity-75' : 'cursor-pointer'
                } ${!isChecked ? 'opacity-60' : ''}`}
                aria-label={`View ${item.filename}`}
              >
                <div
                  className="flex-shrink-0"
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleChecked(item, !isChecked, e);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      e.preventDefault();
                      handleToggleChecked(item, !isChecked, e as any);
                    }
                  }}
                  role="checkbox"
                  tabIndex={0}
                  aria-checked={isChecked}
                  aria-label={`Toggle ${item.filename}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={e => {
                      e.stopPropagation();
                      handleToggleChecked(item, e.target.checked, e as any);
                    }}
                    size="sm"
                    disabled={isLoading}
                    containerClassName="m-0"
                  />
                </div>
                {isLoading ? (
                  <Icon
                    name="LoadingSpinner"
                    className="w-4 h-4 text-xtnd-blue dark:text-xtnd-blue-400 flex-shrink-0 animate-spin"
                  />
                ) : (
                  <Icon
                    name={getFileIcon(item.filename)}
                    className="w-4 h-4 text-xtnd-blue dark:text-xtnd-blue-400 flex-shrink-0"
                  />
                )}
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-white truncate max-w-[200px]">
                    {item.filename}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {fileUtil.formatFileSize(item.fileSize)}
                  </span>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center gap-2 px-3 py-2 bg-xtnd-light dark:bg-xtnd-dark-700 border-2 border-dashed border-xtnd-blue-200 dark:border-xtnd-dark-600 rounded-md hover:border-xtnd-blue dark:hover:border-xtnd-blue-400 hover:bg-xtnd-blue-50 dark:hover:bg-xtnd-dark-600 transition-colors focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-1"
            aria-label="Add attachment"
          >
            <Icon
              name="HiPlus"
              className="w-4 h-4 text-xtnd-blue dark:text-xtnd-blue-400 flex-shrink-0"
            />
          </button>
        </div>
      )}

      <UploadFileDialog isOpen={isOpen} onClose={handleClose} onConfirm={handleConfirm} />
    </div>
  );
};

const UploadFileDialogImpl = ({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (files: File[]) => void;
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [checkedFiles, setCheckedFiles] = useState<Set<number>>(
    new Set(files.map((_, index) => index))
  );

  const handleClose = () => {
    setFiles([]);
    setCheckedFiles(new Set());
    onClose();
  };

  const handleToggleFileChecked = (index: number, checked: boolean) => {
    const newCheckedFiles = new Set(checkedFiles);
    if (checked) {
      newCheckedFiles.add(index);
    } else {
      newCheckedFiles.delete(index);
    }
    setCheckedFiles(newCheckedFiles);
  };

  const handleConfirm = () => {
    const selectedFiles = files.filter((_, index) => checkedFiles.has(index));
    onConfirm(selectedFiles);
    handleClose();
  };

  // Update checked files when files change
  React.useEffect(() => {
    if (files.length > 0) {
      setCheckedFiles(new Set(files.map((_, index) => index)));
    }
  }, [files]);
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="lg"
      className="!max-h-[90vh] overflow-y-auto"
      showCloseButton={false}
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            Upload Attachments
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="space-y-4">
        <FileUpload
          files={files}
          isMultiple={true}
          accept={AR_ATTACHMENT_ACCEPT}
          maxSize={AR_ATTACHMENT_MAX_SIZE}
          onChange={setFiles}
          isDisplayUploadButton={false}
          maxFileNumber={6}
        />
        {files.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-xtnd-dark-600">
            <p className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
              Select files to include:
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {files.map((file, index) => {
                const isChecked = checkedFiles.has(index);
                return (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-md"
                  >
                    <Checkbox
                      checked={isChecked}
                      onChange={e => handleToggleFileChecked(index, e.target.checked)}
                      size="sm"
                      containerClassName="m-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-xtnd-dark dark:text-xtnd-light truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {fileUtil.formatFileSize(file.size)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogBody>

      <DialogFooter className="w-full">
        <Button
          variant="primary"
          onClick={handleConfirm}
          fullWidth
          disabled={checkedFiles.size === 0}
        >
          Confirm ({checkedFiles.size} {checkedFiles.size === 1 ? 'file' : 'files'} selected)
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

const UploadFileDialog = React.memo(UploadFileDialogImpl);
UploadFileDialog.displayName = 'UploadFileDialog';
